
import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface AIQuestionFlowProps {
  description: string;
  problemsStated: string;  // renamed from summary
  questions: string[];
  answers: string[];
  onAnswerChange: (index: number, value: string) => void;
  onMoreQuestions: () => void;
  onFinish: () => Promise<void>;
  onBack: () => void;
  isLoadingQuestions?: boolean;
  isGeneratingSummary?: boolean; // Add Phase 2 loading state
}

const AIQuestionFlow: React.FC<AIQuestionFlowProps> = ({
  description,
  problemsStated,  // renamed from summary
  questions,
  answers,
  onAnswerChange,
  onMoreQuestions,
  onFinish,
  onBack,
  isLoadingQuestions = false,
  isGeneratingSummary = false, // Add Phase 2 loading state
}) => {
  return (
    <div className="space-y-6 animate-fade-in pb-3">
      <Card className="bg-soft-blue/10 border-soft-blue/20 p-6">
        <p className="text-lg font-medium mb-4">
          Got it. Let's figure this out together.
        </p>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-2">
          I'd like to ask you some questions to better understand the situation.
          Feel free to skip any that aren't relevant.
        </p>
      </Card>
      
      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="font-medium mb-2">{question}</p>
            <Textarea
              placeholder="Your answer (optional)"
              value={answers[index] || ''}
              onChange={(e) => onAnswerChange(index, e.target.value)}
              className="w-full mt-2"
            />
          </div>
        ))}
        
        {isLoadingQuestions && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-soft-blue animate-spin mr-2" />
            <p>Generating relevant questions...</p>
          </div>
        )}
      </div>
      
      <div className="flex gap-4 pt-6 mt-6 bg-background pb-4">
        <Button
          variant="secondary"
          className="flex-1 border-soft-blue text-soft-blue"
          onClick={onMoreQuestions}
          disabled={isLoadingQuestions || questions.length >= 15} // Max 3 rounds of questions (15 total)
        >
          Ask more questions
        </Button>
        
        <Button 
          className="flex-1 bg-lime text-black hover:bg-lime/90"
          onClick={async () => {
            try {
              await onFinish();
            } catch (error) {
              console.error('Error in onFinish:', error);
            }
          }}
          disabled={isLoadingQuestions || isGeneratingSummary}
        >
          {isGeneratingSummary ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Continuing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </div>
  );
};

export default AIQuestionFlow;
